import React, {forwardRef, useMemo} from 'react';
import type {BoxProps, IconProps} from '@tempo/core';
import {Box, HStack, Text} from '@tempo/core';
import {NavLink} from '@tempo/nextjs';
import {useRouter} from 'next/router';

type NavItemProps = {
	label: string;
	url?: string;
	pattern?: RegExp;
	icon?: React.ElementType<
		Omit<IconProps, 'size'> & {
			size?: 'small' | 'medium';
		}
	>;
	iconElement?: React.ReactElement;
	externalLink?: boolean;
	onClick?: () => void;
	collapsed?: boolean;
} & BoxProps;

const NavItem = forwardRef<HTMLButtonElement | HTMLAnchorElement, NavItemProps>(
	(
		{
			label,
			url,
			pattern,
			externalLink,
			icon: IconComponent,
			iconElement,
			collapsed,
			onClick,
			...rest
		},
		ref,
	) => {
		const router = useRouter();

		const active = useMemo(
			() => (pattern ? pattern.test(router.pathname) : false),
			[pattern, router.pathname],
		);

		const commonProps = {
			borderRadius: 's',
			py: {base: 'spacing.s', md: 'spacing.xs'},
			px: 'spacing.m',
			_hover: {
				bg: 'background.neutral.tertiary.default',
			},
			role: 'group',
			cursor: 'pointer',
		};

		const content = (
			<HStack spacing="spacing.s">
				<Box
					as="span"
					color={
						active
							? 'icon.neutral.primary.selected'
							: 'icon.neutral.secondary.default'
					}
					_groupHover={{
						color: active
							? 'icon.neutral.primary.selected'
							: 'icon.neutral.primary.default',
					}}
				>
					{IconComponent ? (
						<IconComponent boxSize="icon.s" size="medium" />
					) : (
						iconElement
					)}
				</Box>

				<Text
					variant="body.small.strong"
					color={
						active
							? 'text.neutral.primary.selected'
							: 'text.neutral.secondary.default'
					}
					_groupHover={{
						color: active
							? 'text.neutral.primary.selected'
							: 'text.neutral.primary.default',
					}}
					textAlign="left"
					opacity={collapsed ? 0 : 1}
					width={collapsed ? 0 : 'auto'}
					noOfLines={1}
					transition="height, width, opacity 200ms"
				>
					{label}
				</Text>
			</HStack>
		);

		if (!url) {
			return (
				<Box
					ref={ref as React.Ref<HTMLButtonElement>}
					as="button"
					type="button"
					{...commonProps}
					onClick={onClick}
					{...rest}
				>
					{content}
				</Box>
			);
		}

		if (externalLink) {
			return (
				<Box
					ref={ref as React.Ref<HTMLAnchorElement>}
					as={NavLink}
					href={url}
					target="_blank"
					rel="noreferrer"
					{...commonProps}
					onClick={onClick}
					{...rest}
				>
					{content}
				</Box>
			);
		}

		return (
			<Box
				as={NavLink}
				ref={ref as React.Ref<HTMLDivElement>}
				href={url}
				onClick={onClick}
				{...commonProps}
				{...rest}
			>
				{content}
			</Box>
		);
	},
);

NavItem.displayName = 'NavItem';

export default NavItem;
